import { Controller, useFormContext } from "react-hook-form";
import { TextField as ReusableTextField } from "@flightright/reusable-components";
import { useTheme } from "@mui/material";
import { Theme } from "@mui/material";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { FieldSubtype } from "@/core/types/types";
import { FieldProps } from ".";
import { HOTJAR_ATTRIBUTES as extraProps } from "@/core/data";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";
import { useEffect } from "react";
import { useInvalidForm } from "@/core/hooks/useInvalidForm/useInvalidForm";

const TextField = ({ field, initialValue }: FieldProps) => {
  const theme: Theme = useTheme();

  const { defaultValue } = field;
  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const { hasError, errorMessage, errorTranslationKey } = useFieldError(
    errors,
    field
  );
  const initialText = useDefaultValue(initialValue ?? defaultValue);

  const { isInvalid, invalidMessage } = useInvalidForm(field);

  useEffect(() => {
    if (initialText) {
      setValue(field.key, initialText);
    }
  }, [initialText]);

  return (
    <Controller
      name={field.key}
      control={control}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: t(`validate.${errorTranslationKey}.required.message`),
        },
        pattern: {
          value: new RegExp(field.validate?.regex || ".*"),
          message: t(`validate.${errorTranslationKey}.pattern.message`),
        },
      }}
      render={({ field: fieldProps }) => (
        <ReusableTextField
          id={field.key}
          label={t(field.label)}
          textFieldStyles={{
            margin: theme.spacing(1, 0),
          }}
          multiline={field.properties?.subtype === FieldSubtype.TextArea}
          rows={field.properties?.rows}
          minRows={field.properties?.minRows}
          maxRows={field.properties?.maxRows}
          placeholder={field.properties?.placeholderText}
          disabled={field.disabled}
          error={hasError || isInvalid}
          helperText={errorMessage || invalidMessage}
          value={fieldProps.value || ""}
          onChange={fieldProps.onChange}
          {...extraProps}
        />
      )}
    />
  );
};

export default TextField;
